import '@ingka/badge/style.scss'
import '@ingka/toast/style.scss'
import '@ingka/aspect-ratio-box/style.scss'
import '@ingka/base/style.scss'
import '@ingka/card/style.scss'
import '@ingka/commercial-message/dist/style.css'
import '@ingka/commercial-message/style.scss'
import '@ingka/forms/style.scss'
import '@ingka/grid/style.scss'
import '@ingka/hyperlink/style.scss'
import '@ingka/image/style.scss'
import '@ingka/inline-message/dist/style.css'
import '@ingka/list/style.scss'
import '@ingka/modal/dist/style.css'
import '@ingka/price-module/dist/style.css'
import '@ingka/price-module/style.scss'
import '@ingka/price/dist/style.css'
import '@ingka/price/style.scss'
import '@ingka/search/style.scss'
import '@ingka/skeleton/style.scss'
import '@ingka/tabs/style.scss'
import '@ingka/text/style.scss'
import '@ingka/tooltip/dist/style.css'
import '@ingka/accordion/style.scss'
import '@ingka/pill/style.scss'
import '@ingka/product-identifier/style.scss'
import '@ingka/switch/style.scss'
import '@ingka/list-view/style.scss'
import '@ingka/status/style.scss'
import '@ingka/expander/style.scss'
import '@ingka/carousel/style.scss'
import '@ingka/table/style.scss'
import '@ingka/typography/style.scss'
import '@ingka/variables/style.scss'
import '@ingka/svg-icon/dist/style.css'
import '@ingka/button/dist/style.css'
import '@ingka/carousel/dist/style.css'
import '@ingka/focus/dist/style.css'
// i18n configuration
import '@/i18n/config'
// uncomment/comment line below to enable/disable tailwind
import '../globals.css'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import axios from 'axios'
import type { AppProps } from 'next/app'
import { useEffect, useState } from 'react'

import { Layout } from '@/components/layout/layout'
import { TurnstileProvider } from '@/contexts/turnstile-context/turnstile-context'
import { env } from '@/env.mjs'
import { AnalyticsEvent } from '@/types'

const queryClient = new QueryClient()

function SafeHydrate({ children }: { children: React.ReactNode }) {
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  return <>{isClient ? children : null}</>
}

function AsIsShopRoot({ Component, pageProps, router }: AppProps) {
  const mock = env.NEXT_PUBLIC_PREVIEW_MODE === 'true' || router.isPreview

  // Set defaults for Axios
  axios.defaults.headers.common['x-client-id'] = env.NEXT_PUBLIC_CLIENT_ID

  useEffect(() => {
    if (!window.ikea) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      window.ikea = {} as any
    }

    // Initialize window.ikea.recommerce namespace
    if (!window.ikea.recommerce) {
      window.ikea.recommerce = {}
    }
    // Initialize analytics
    if (
      env.NEXT_PUBLIC_ENV !== 'PRO' &&
      (!window?.sendEvent || typeof window.sendEvent !== 'function')
    ) {
      window.sendEvent = (event: AnalyticsEvent) => {
        if (!window?.dataLayer) {
          window.dataLayer = []
        }
        window.dataLayer?.push(event)
        console.log('Google Analytics send payload:')
        console.table(event)
      }
    }
  }, [])

  // Bridge Turnstile to window.ikea.recommerce
  useEffect(() => {
    const bridgeTurnstile = () => {
      if (window.ikea?.recommerce?.turnstile) {
        return true
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cloudflareAPI = (window as any).turnstile
      if (cloudflareAPI && window.ikea?.recommerce) {
        window.ikea.recommerce.turnstile = cloudflareAPI
        return true
      }
      return false
    }

    // Try to bridge immediately
    if (bridgeTurnstile()) {
      return
    }

    // If not available yet, poll for it
    const interval = setInterval(() => {
      if (bridgeTurnstile()) {
        clearInterval(interval)
      }
    }, 100)

    // Cleanup
    return () => clearInterval(interval)
  }, [])

  const turnstileSiteKey = env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

  return (
    <SafeHydrate>
      <QueryClientProvider client={queryClient}>
        {turnstileSiteKey ? (
          <TurnstileProvider>
            <Layout mock={mock}>
              <Component {...pageProps} />
            </Layout>
          </TurnstileProvider>
        ) : (
          <Layout mock={mock}>
            <Component {...pageProps} />
          </Layout>
        )}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </SafeHydrate>
  )
}

export default AsIsShopRoot
